import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, Star, Zap, Crown, Sparkles, ArrowRight, Users } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";
const Packages = () => {
  const packages = [{
    name: "Growth",
    price: "₹5,00,000",
    icon: Star,
    badge: "Best Value",
    badgeColor: "bg-blue-500 text-white border-blue-600",
    description: "Perfect for growing companies ready to own their content engine",
    features: ["2-person trained content team", "Core systems + SOPs setup", "6-month content calendar", "ClickUp workspace setup", "2 months optimization support", "90-day independence guarantee"],
    cta: "Start Building Team",
    highlight: false,
    teamSize: "2 People",
    color: "blue"
  }, {
    name: "Scale",
    price: "₹7,50,000",
    icon: Zap,
    badge: "Most Popular",
    badgeColor: "bg-blue-600 text-white border-blue-700",
    description: "Scale-ready solution with advanced systems and analytics",
    features: ["3-person expert content team", "Advanced templates + analytics", "12-month strategic calendar", "Custom workflow automation", "3 months optimization support", "Quarterly strategy reviews", "Performance dashboards"],
    cta: "Scale My Content",
    highlight: true,
    teamSize: "3 People",
    color: "blue"
  }, {
    name: "Enterprise",
    price: "Custom",
    icon: Crown,
    badge: "Custom",
    badgeColor: "bg-blue-700 text-white border-blue-800",
    description: "Enterprise-grade content machine for multi-brand operations",
    features: ["4–5 person specialist team", "Enterprise workflow systems", "Multi-brand content support", "Custom tool integrations", "6 months ongoing support", "Dedicated account manager", "Priority feature requests"],
    cta: "Get Custom Quote",
    highlight: false,
    teamSize: "4-5 People",
    color: "blue"
  }];
  return <section id="packages" className="section-spacing bg-gradient-to-b from-primary/5 to-background scroll-mt-24 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-1/4 left-0 w-72 h-72 bg-accent/5 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <ScrollReveal delay={100}>
          <div className="text-center mb-16 lg:mb-20">
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 glass-panel rounded-full border border-primary/20">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-primary">Ownership Packages</span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl lg:text-6xl font-display font-bold mb-6 lg:mb-8 leading-tight text-foreground">
              Choose Your{" "}
              <span className="bg-gradient-brand bg-clip-text text-transparent">Investment Packages</span>
            </h2>
            
            <p className="text-lg lg:text-xl text-muted-foreground max-w-4xl mx-auto leading-relaxed">
              One-time investment, lifetime ownership. You own the people, systems, and outputs—no retainers, 
              no dependency, just pure compounding growth.
            </p>
          </div>
        </ScrollReveal>

        <div className="grid lg:grid-cols-3 gap-8 lg:gap-6 max-w-7xl mx-auto">
          {packages.map((pkg, index) => {
          const IconComponent = pkg.icon;
          return <ScrollReveal key={pkg.name} delay={200 + index * 100}>
                <Card className={`relative glass-panel p-6 lg:p-8 rounded-2xl transition-all duration-300 hover:-translate-y-2 h-full border group ${pkg.highlight ? 'border-primary/30 shadow-lg shadow-primary/10' : 'border-border hover:border-primary/20'}`}>
                  {/* Background glow effect */}
                  <div className={`absolute inset-0 bg-gradient-to-br from-${pkg.color}/10 to-${pkg.color}/5 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                  
                  {/* Badge */}
                  {pkg.badge && <Badge className={`absolute -top-3 left-1/2 transform -translate-x-1/2 ${pkg.badgeColor} px-3 py-1 text-xs font-semibold`}>
                      {pkg.badge}
                    </Badge>}

                  {/* Header */}
                  <div className="text-center mb-6 pt-4">
                    <IconComponent className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-500/20 border border-blue-500/40 p-4 box-content text-blue-500" />
                    
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Users className="w-4 h-4 text-blue-500" />
                      <span className="text-sm font-medium text-blue-500">{pkg.teamSize}</span>
                    </div>
                    
                    <h3 className="text-2xl font-display font-bold mb-3 text-foreground">{pkg.name}</h3>
                    
                    {pkg.price === "Custom" ? 
                      <div className="text-3xl font-display font-bold bg-gradient-brand bg-clip-text text-transparent mb-3">Custom Pricing</div> : 
                      <div className="text-3xl lg:text-4xl font-display font-bold bg-gradient-brand bg-clip-text text-transparent mb-3">{pkg.price}</div>
                    }
                    
                    <p className="text-sm text-muted-foreground leading-relaxed">{pkg.description}</p>
                  </div>

                  {/* Features */}
                  <div className="space-y-3 mb-6 flex-1">
                    {pkg.features.map((feature, featureIndex) => 
                      <div key={featureIndex} className="flex items-start gap-3">
                        <Check className="w-3 h-3 text-blue-500 p-1 rounded-full mt-0.5 bg-blue-500/20 box-content" />
                        <span className="text-sm text-foreground/90 leading-relaxed">{feature}</span>
                      </div>
                    )}
                  </div>

                  {/* Value Proposition */}
                  <div className="p-4 rounded-lg mb-6 border bg-blue-500/5 border-blue-500/20">
                    <p className="text-sm font-medium text-center text-blue-500">
                      Complete ownership • No dependencies • Lifetime value
                    </p>
                  </div>

                  {/* CTA */}
                  <Button variant={pkg.highlight ? "hero" : "outline"} size="lg" className="w-full text-sm font-semibold min-h-[48px] group" onClick={() => window.location.href = '/contact'}>
                    {pkg.cta}
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Card>
              </ScrollReveal>;
        })}
        </div>

        {/* Bottom CTA Section */}
        <ScrollReveal delay={600}>
          <div className="mt-16 lg:mt-20 text-center">
            <div className="max-w-4xl mx-auto p-6 lg:p-8 glass-panel rounded-2xl border border-accent/20">
              <div className="grid md:grid-cols-3 gap-6 items-center">
                <div className="md:col-span-2 text-left">
                  <h3 className="text-xl lg:text-2xl font-display font-bold mb-2 text-foreground">
                    Not sure which package fits your vision?
                  </h3>
                  <p className="text-sm lg:text-base text-muted-foreground">
                    Book a 15-minute strategy call to get a custom recommendation based on your current setup and growth goals.
                  </p>
                </div>
                <div>
                  <Button variant="hero" size="lg" className="w-full md:w-auto text-sm font-semibold min-h-[48px]" onClick={() => window.location.href = '/contact'}>
                    Get Custom Recommendation
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </ScrollReveal>

        {/* Trust indicators */}
        <ScrollReveal delay={700}>
          <div className="mt-12 text-center">
            <div className="flex flex-wrap justify-center items-center gap-8 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                <span>47+ Teams Built</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
                <span>90-Day Guarantee</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span>100% Ownership</span>
              </div>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>;
};
export default Packages;